import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface ComplianceTask {
  id: string;
  company_id: string;
  title: string;
  description: string | null;
  priority: 'p0' | 'p1' | 'p2' | 'p3';
  status: 'not_started' | 'in_progress' | 'at_risk' | 'blocked' | 'complete';
  primary_assignee_id: string | null;
  watcher_ids: string[];
  policy_id: string | null;
  checklist_item_id: string | null;
  department_id: string | null;
  program_id: string | null;
  control_id: string | null;
  evidence_item_id: string | null;
  program_phase: 'policies' | 'controls' | 'evidence' | 'team' | 'audit_ready' | null;
  reference_type: 'program' | 'policy' | 'control' | 'evidence' | 'training' | null;
  due_date: string | null;
  completed_at: string | null;
  completed_by: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Related data
  primary_assignee?: {
    id: string;
    name: string;
    email: string;
  };
  department?: {
    id: string;
    name: string;
    color: string;
  };
  policy?: {
    id: string;
    title: string;
  };
  program?: {
    id: string;
    name: string;
    framework: string;
  } | null;
  control?: {
    id: string;
    control_id: string;
    title: string;
  } | null;
}

export interface TaskFilters {
  status?: ComplianceTask['status'] | 'all';
  priority?: ComplianceTask['priority'] | 'all';
  assignee?: string | 'all';
  department?: string | 'all';
  program?: string | 'all';
  phase?: ComplianceTask['program_phase'] | 'all';
  search?: string;
}

export function useComplianceTasks(filters?: TaskFilters) {
  const { profile } = useAuth();
  const [tasks, setTasks] = useState<ComplianceTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    if (!profile?.company_id) return;

    try {
      setLoading(true);
      setError(null);

      // Use a single optimized query with joins to fetch all related data at once
      let query = supabase
        .from('compliance_tasks')
        .select(`
          *,
          primary_assignee:profiles!compliance_tasks_primary_assignee_id_fkey(id, name, email),
          department:departments!compliance_tasks_department_id_fkey(id, name, color),
          policy:policies!compliance_tasks_policy_id_fkey(id, title),
          program:programs!compliance_tasks_program_id_fkey(id, name, framework),
          control:framework_controls!compliance_tasks_control_id_fkey(id, control_id, title)
        `)
        .eq('company_id', profile.company_id)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.priority && filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }

      if (filters?.assignee && filters.assignee !== 'all') {
        query = query.eq('primary_assignee_id', filters.assignee);
      }

      if (filters?.department && filters.department !== 'all') {
        query = query.eq('department_id', filters.department);
      }

      if (filters?.program && filters.program !== 'all') {
        query = query.eq('program_id', filters.program);
      }

      if (filters?.phase && filters.phase !== 'all') {
        query = query.eq('program_phase', filters.phase);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Map the data to our ComplianceTask type (no need for n+1 queries!)
      const tasksWithRelations: ComplianceTask[] = ((data || []) as ComplianceTask[]).map((task) => ({
        ...task,
        primary_assignee: task.primary_assignee || null,
        department: task.department || null,
        policy: task.policy || null,
        program: task.program || null,
        control: task.control || null,
      }));

      // Filter by search if provided
      let filteredData = tasksWithRelations;
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        filteredData = filteredData.filter(
          (task) =>
            task.title.toLowerCase().includes(searchLower) ||
            (task.description && task.description.toLowerCase().includes(searchLower))
        );
      }

      setTasks(filteredData);
    } catch (err) {
      console.error('Error fetching compliance tasks:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [profile?.company_id, filters]);

  useEffect(() => {
    if (!profile?.company_id) return;

    fetchTasks();

    // Set up real-time listener
    const channel = supabase
      .channel('compliance-tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'compliance_tasks',
          filter: `company_id=eq.${profile.company_id}`,
        },
        () => {
          fetchTasks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.company_id, JSON.stringify(filters)]);

  const createTask = async (taskData: Partial<ComplianceTask>) => {
    if (!profile?.company_id) throw new Error('Company ID not found');

    const { data, error: createError } = await supabase
      .from('compliance_tasks')
      .insert({
        ...taskData,
        company_id: profile.company_id,
        created_by: profile.id,
      })
      .select('*')
      .single();

    if (createError) throw createError;
    await fetchTasks();
    return data;
  };

  const updateTask = async (taskId: string, updates: Partial<ComplianceTask>) => {
    const { data, error: updateError } = await supabase
      .from('compliance_tasks')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', taskId)
      .select('*')
      .single();

    if (updateError) throw updateError;
    await fetchTasks();
    return data;
  };

  const completeTask = async (taskId: string) => {
    if (!profile?.id) throw new Error('User not found');

    return updateTask(taskId, {
      status: 'complete',
      completed_at: new Date().toISOString(),
      completed_by: profile.id,
    });
  };

  const deleteTask = async (taskId: string) => {
    const { error: deleteError } = await supabase
      .from('compliance_tasks')
      .delete()
      .eq('id', taskId);

    if (deleteError) throw deleteError;
    await fetchTasks();
  };

  return {
    tasks,
    loading,
    error,
    refetch: fetchTasks,
    createTask,
    updateTask,
    completeTask,
    deleteTask,
  };
}

export function useComplianceTask(taskId: string | undefined) {
  const { profile } = useAuth();
  const [task, setTask] = useState<ComplianceTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId || !profile?.company_id) {
      setLoading(false);
      return;
    }

    fetchTask();
  }, [taskId, profile?.company_id]);

  const fetchTask = async () => {
    if (!taskId || !profile?.company_id) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('compliance_tasks')
        .select(`
          *,
          primary_assignee:profiles!compliance_tasks_primary_assignee_id_fkey(id, name, email),
          department:departments!compliance_tasks_department_id_fkey(id, name, color),
          policy:policies!compliance_tasks_policy_id_fkey(id, title)
        `)
        .eq('id', taskId)
        .eq('company_id', profile.company_id)
        .single();

      if (fetchError) throw fetchError;

      setTask(data as ComplianceTask);
    } catch (err) {
      console.error('Error fetching compliance task:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const updateTask = async (taskId: string, updates: Partial<ComplianceTask>) => {
    const { data, error: updateError } = await supabase
      .from('compliance_tasks')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', taskId)
      .select('*')
      .single();

    if (updateError) throw updateError;
    await fetchTask();
    return data;
  };

  const completeTask = async (taskId: string, userId: string) => {
    return updateTask(taskId, {
      status: 'complete',
      completed_at: new Date().toISOString(),
      completed_by: userId,
    });
  };

  const deleteTask = async (taskId: string) => {
    const { error: deleteError } = await supabase
      .from('compliance_tasks')
      .delete()
      .eq('id', taskId);

    if (deleteError) throw deleteError;
  };

  return {
    task,
    loading,
    error,
    refetch: fetchTask,
    updateTask,
    completeTask,
    deleteTask,
  };
}
